import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as THREE from 'three';
import { useFrame, useThree, Canvas, createPortal, extend, useLoader } from '@react-three/fiber';
import { OrbitControls, Stats, useGLTF, Instances, Instance } from '@react-three/drei';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';
import { Suspense } from 'react';

// New Flame shader based on provided example
const FlameShader = {
  uniforms: {
    time: { value: 0 }
  },
  vertexShader: `
    uniform float time;
    varying vec2 vUv;
    varying float hValue;

    //https://thebookofshaders.com/11/
    // 2D Random
    float random (in vec2 st) {
        return fract(sin(dot(st.xy,
                             vec2(12.9898,78.233)))
                     * 43758.5453123);
    }

    // 2D Noise based on Morgan McGuire @morgan3d
    // https://www.shadertoy.com/view/4dS3Wd
    float noise (in vec2 st) {
        vec2 i = floor(st);
        vec2 f = fract(st);

        // Four corners in 2D of a tile
        float a = random(i);
        float b = random(i + vec2(1.0, 0.0));
        float c = random(i + vec2(0.0, 1.0));
        float d = random(i + vec2(1.0, 1.0));

        // Smooth Interpolation

        // Cubic Hermine Curve.  Same as SmoothStep()
        vec2 u = f*f*(3.0-2.0*f);
        // u = smoothstep(0.,1.,f);

        // Mix 4 coorners percentages
        return mix(a, b, u.x) +
                (c - a)* u.y * (1.0 - u.x) +
                (d - b) * u.x * u.y;
    }

    void main() {
      vUv = uv;
      vec3 pos = position;

      pos *= vec3(0.8, 2, 0.725);
      hValue = position.y;
      //float sinT = sin(time * 2.) * 0.5 + 0.5;
      float posXZlen = length(position.xz);

      pos.y *= 1. + (cos((posXZlen + 0.25) * 3.1415926) * 0.25 + noise(vec2(0, time)) * 0.125 + noise(vec2(position.x + time, position.z + time)) * 0.5) * position.y; // flame height

      pos.x += noise(vec2(time * 2., (position.y - time) * 4.0)) * hValue * 0.0312; // flame trembling
      pos.z += noise(vec2((position.y - time) * 4.0, time * 2.)) * hValue * 0.0312; // flame trembling

      gl_Position = projectionMatrix * modelViewMatrix * vec4(pos,1.0);
    }
  `,
  fragmentShader: `
    varying float hValue;
    varying vec2 vUv;

    // honestly stolen from https://www.shadertoy.com/view/4dsSzr
    vec3 heatmapGradient(float t) {
      return clamp((pow(t, 1.5) * 0.8 + 0.2) * vec3(smoothstep(0.0, 0.35, t) + t * 0.5, smoothstep(0.5, 1.0, t), max(1.0 - t * 1.7, t * 7.0 - 6.0)), 0.0, 1.0);
    }

    void main() {
      float v = abs(smoothstep(0.0, 0.4, hValue) - 1.);
      float alpha = (1. - v) * 0.99; // bottom transparency
      alpha -= 1. - smoothstep(1.0, 0.97, hValue); // tip transparency
      gl_FragColor = vec4(heatmapGradient(smoothstep(0.0, 0.3, hValue)) * vec3(0.95,0.95,0.4), alpha) ;
      gl_FragColor.rgb = mix(vec3(0,0,1), gl_FragColor.rgb, smoothstep(0.0, 0.3, hValue)); // blueish for bottom
      gl_FragColor.rgb += vec3(1, 0.9, 0.5) * (1.25 - vUv.y); // make the midst brighter
      gl_FragColor.rgb = mix(gl_FragColor.rgb, vec3(0.66, 0.32, 0.03), smoothstep(0.95, 1., hValue)); // tip
    }
  `
};

// Custom shader material class for flames (for both front and back sides)
class FlameMaterial extends THREE.ShaderMaterial {
  constructor(isFrontSide = true) {
    super({
      uniforms: THREE.UniformsUtils.clone(FlameShader.uniforms),
      vertexShader: FlameShader.vertexShader,
      fragmentShader: FlameShader.fragmentShader,
      transparent: true,
      side: isFrontSide ? THREE.FrontSide : THREE.BackSide
    });
  }
}

// Register the custom material with R3F
extend({ FlameMaterial });

// Material components with optimized texture loading
function Floor() {
  // Use useMemo to create the material only once
  const floorMat = useMemo(() => new THREE.MeshStandardMaterial({
    roughness: 0.8,
    color: 0xffffff,
    metalness: 0.2,
    bumpScale: 1
  }), []);

  useEffect(() => {
    // Use a shared loader for all textures
    const textureLoader = new THREE.TextureLoader();
    const baseSettings = {
      wrapS: THREE.RepeatWrapping,
      wrapT: THREE.RepeatWrapping,
      anisotropy: 2, // Reduced anisotropy for better performance
      repeat: new THREE.Vector2(10, 24)
    };
    
    // Load textures in parallel
    Promise.all([
      new Promise(resolve => textureLoader.load('/textures/hardwood2_diffuse.jpg', resolve)),
      new Promise(resolve => textureLoader.load('/textures/hardwood2_bump.jpg', resolve)),
      new Promise(resolve => textureLoader.load('/textures/hardwood2_roughness.jpg', resolve))
    ]).then(([diffuseMap, bumpMap, roughnessMap]) => {
      // Apply settings to all textures
      [diffuseMap, bumpMap, roughnessMap].forEach(map => {
        map.wrapS = baseSettings.wrapS;
        map.wrapT = baseSettings.wrapT;
        map.anisotropy = baseSettings.anisotropy;
        map.repeat.copy(baseSettings.repeat);
      });
      
      // Set color space only for the diffuse map
      diffuseMap.colorSpace = THREE.SRGBColorSpace;
      
      // Apply maps to material
      floorMat.map = diffuseMap;
      floorMat.bumpMap = bumpMap;
      floorMat.roughnessMap = roughnessMap;
      floorMat.needsUpdate = true;
    });
  }, [floorMat]);

  return (
    <mesh rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
      <planeGeometry args={[20, 20]} />
      <primitive object={floorMat} attach="material" />
    </mesh>
  );
}

// Lower-poly Earth
function Earth() {
  const ballMat = useMemo(() => new THREE.MeshStandardMaterial({
    color: 0xffffff,
    roughness: 0.5,
    metalness: 1.0
  }), []);

  useEffect(() => {
    const textureLoader = new THREE.TextureLoader();
    
    // Load earth texture with lower resolution
    Promise.all([
      new Promise(resolve => textureLoader.load('/textures/planets/earth_atmos_2048.jpg', resolve)),
      new Promise(resolve => textureLoader.load('/textures/planets/earth_specular_2048.jpg', resolve))
    ]).then(([diffuseMap, metalnessMap]) => {
      // Apply settings with reduced anisotropy
      [diffuseMap, metalnessMap].forEach(map => {
        map.anisotropy = 2;
      });
      
      diffuseMap.colorSpace = THREE.SRGBColorSpace;
      
      ballMat.map = diffuseMap;
      ballMat.metalnessMap = metalnessMap;
      ballMat.needsUpdate = true;
    });
  }, [ballMat]);

  return (
    <mesh position={[1, 0.25, 1]} rotation={[0, Math.PI, 0]} castShadow>
      <sphereGeometry args={[0.25, 16, 16]} /> {/* Reduced geometry complexity */}
      <primitive object={ballMat} attach="material" />
    </mesh>
  );
}

function TelevisionModel({ enableScreenMaterial = false }) {
  const gltf = useLoader(GLTFLoader, './tv.glb');

  useEffect(() => {
    if (gltf) {
      // Initial transform adjustments (only once)
      gltf.scene.scale.set(4, 4, 4);
      gltf.scene.position.set(0, 0.7, -1);
      gltf.scene.rotation.set(0, -Math.PI / 2, 0);

      // Texture loader kept outside conditional so that the texture is ready when needed
      const textureLoader = new THREE.TextureLoader();

      // Traverse every time enableScreenMaterial changes so we can swap materials dynamically
      gltf.scene.traverse((child) => {
        if (child.isMesh) {
          child.castShadow = true;
          child.receiveShadow = true;

          // Handle screen material swap with a 3-second delay before adding the texture
          if (child.name === 'Screen' || child.material?.name?.includes('screen')) {
            // Always start with a simple black material
            child.material = new THREE.MeshStandardMaterial({
              color: 0x000000,
              roughness: 1,
              metalness: 0
            });

            if (enableScreenMaterial) {
              // After 3 seconds, replace the black material with the emissive screen material
              setTimeout(() => {
                const screenTexture = textureLoader.load('/frame.jpg', (texture) => {
                  texture.wrapS = THREE.ClampToEdgeWrapping;
                  texture.wrapT = THREE.ClampToEdgeWrapping;
                  texture.flipY = false;
                  texture.center = new THREE.Vector2(0.5, 0.5);
                  texture.colorSpace = THREE.SRGBColorSpace;
                });

                const emissiveMaterial = new THREE.MeshStandardMaterial({
                  map: screenTexture,
                  emissive: new THREE.Color(0xffffff),
                  emissiveMap: screenTexture,
                  emissiveIntensity: 0.2,
                  toneMapped: false,
                  transparent: false
                });

                child.material = emissiveMaterial;
              }, 3000);
            }
          }
        }
      });
    }
  }, [gltf, enableScreenMaterial]);

  return <primitive object={gltf.scene} position={[0, 0, -3]} rotation={[0, 0, 0]} />;
}

function Television({ enableScreenMaterial }) {
  return (
    <Suspense fallback={null}>
      <TelevisionModel enableScreenMaterial={enableScreenMaterial} />
    </Suspense>
  );
}

// Small util component that fades opacity of all child materials when `start` becomes true
function FadeIn({ children, duration = 1, start = false }) {
  const groupRef = useRef();
  const progress = useRef(0);

  useFrame((_, delta) => {
    if (!start || progress.current >= 1) return;
    progress.current = Math.min(progress.current + delta / duration, 1);
    const alpha = progress.current;
    if (groupRef.current) {
      groupRef.current.traverse((obj) => {
        if (obj.material && obj.material.opacity !== undefined) {
          obj.material.transparent = true;
          obj.material.opacity = alpha;
          obj.material.needsUpdate = true;
        }
      });
    }
  });

  // Keep invisible until fade starts
  return <group ref={groupRef} visible={start}>{children}</group>;
}

// Instanced cubes for better performance
function Cubes() {
  const cubeMat = useMemo(() => new THREE.MeshStandardMaterial({
    roughness: 0.7,
    color: 0xffffff,
    bumpScale: 1,
    metalness: 0.2
  }), []);
  
  const positions = useMemo(() => [
    [0, 0, -1]
  ], []);

  useEffect(() => {
    const textureLoader = new THREE.TextureLoader();
    
    Promise.all([
      new Promise(resolve => textureLoader.load('/textures/brick_diffuse.jpg', resolve)),
      new Promise(resolve => textureLoader.load('/textures/brick_bump.jpg', resolve))
    ]).then(([diffuseMap, bumpMap]) => {
      // Apply settings with reduced quality
      [diffuseMap, bumpMap].forEach(map => {
        map.wrapS = THREE.RepeatWrapping;
        map.wrapT = THREE.RepeatWrapping;
        map.anisotropy = 2;
        map.repeat.set(1, 1);
      });
      
      diffuseMap.colorSpace = THREE.SRGBColorSpace;
      
      cubeMat.map = diffuseMap;
      cubeMat.bumpMap = bumpMap;
      cubeMat.needsUpdate = true;
    });
  }, [cubeMat]);

  return (
    <instancedMesh args={[null, null, positions.length]} castShadow>
      <boxGeometry args={[5, 2, 2]} />
      <primitive object={cubeMat} attach="material" />
      {positions.map((pos, i) => (
        <group key={i} position={pos} onUpdate={self => {
          self.updateMatrix();
          self.matrix.toArray(self.parent.instanceMatrix.array, i * 16);
          self.parent.instanceMatrix.needsUpdate = true;
        }} />
      ))}
    </instancedMesh>
  );
}

// Individual candle with new design
function Candle({ position, index = 0, power = 40 }) {
  const flameMaterialFront = useRef();
  const flameMaterialBack = useRef();
  const candleLight = useRef();
  const candleLight2 = useRef();
  const candleRef = useRef();
  
  // Shape paths for candle components
  const casePath = useMemo(() => {
    const path = new THREE.Path();
    path.moveTo(0, 0);
    path.lineTo(0, 0);
    path.absarc(0.9, 0.5, 0.25, Math.PI * 1.5, Math.PI * 2);
    path.lineTo(1.15, 5.5); // Even thinner profile with same height
    path.lineTo(1.14, 5.5); // Adjusted this point to match the new height
    path.lineTo(1.1, 0.5);
    return path.getPoints();
  }, []);
  
  const paraffinPath = useMemo(() => {
    const path = new THREE.Path();
    path.moveTo(0, 3.0); // Moved to near the top of the case
    path.lineTo(0, 3.0);
    path.absarc(0.9, 3.0, 0.24, Math.PI * 1.5, Math.PI * 2); // Adjusted to match case width
    path.lineTo(1.14, 3.0); // Matches the case width
    path.absarc(1.14, 3.0, 0.1, Math.PI * 1.5, Math.PI * 2); // Slight curve at the top
    return path.getPoints();
  }, []);
  
  // Animate flame and lights
  useFrame(({ clock }) => {
    const time = clock.getElapsedTime();
    
    // Update flame materials
    if (flameMaterialFront.current) {
      flameMaterialFront.current.uniforms.time.value = time;
    }
    if (flameMaterialBack.current) {
      flameMaterialBack.current.uniforms.time.value = time;
    }
    
    // Animate candle lights
    if (candleLight2.current) {
      candleLight2.current.position.x = Math.sin(time * Math.PI) * 0.25;
      candleLight2.current.position.z = Math.cos(time * Math.PI * 0.75) * 0.25;
      candleLight2.current.intensity = 2 + Math.sin(time * Math.PI * 2) * Math.cos(time * Math.PI * 1.5) * 0.25;
    }
    
    // Slight candle movement
    if (candleRef.current) {
      candleRef.current.rotation.x = Math.sin(time * 0.2 + index) * 0.01;
      candleRef.current.rotation.z = Math.cos(time * 0.3 + index) * 0.01;
      
      // Add floating effect - gentle hovering above the ground
      const baseHeight = 2; // Base floating height
      const floatHeight = 0.05; // How much to move up and down
      candleRef.current.position.y = baseHeight + Math.sin(time * 0.8 + index * 0.8) * floatHeight;
    }
  });
  
  return (
    <group position={position}>
      <group ref={candleRef} scale={0.05}>
        {/* Candle metal case */}
        <mesh castShadow position={[0, 0, 0]}>
          <latheGeometry args={[casePath, 64]} />
          <meshStandardMaterial color="silver" />
        </mesh>
        
  
        
        {/* Candlewick */}
        <mesh position={[0, 5.4, 0]}>
          <cylinderGeometry args={[0.0625, 0.0625, 0.5, 8]} />
          <meshBasicMaterial color="black" />
        </mesh>
        
        {/* Flame */}
        <group position={[0, 5.8, 0]}>
          {/* Back flame */}
          <mesh>
            <sphereGeometry args={[0.5, 32, 32]} position={[0, 0.5, 0]} />
            <flameMaterial ref={flameMaterialBack} isFrontSide={false} />
          </mesh>
          
          {/* Front flame */}
          <mesh>
            <sphereGeometry args={[0.5, 32, 32]} position={[0, 0.5, 0]} />
            <flameMaterial ref={flameMaterialFront} isFrontSide={true} />
          </mesh>
        </group>
        
        {/* Candle lights */}
        <pointLight 
          ref={candleLight}
          position={[0, 3, 0]}
          color={0xffaa33}
          intensity={1}
          distance={5}
          decay={2}
          castShadow={index < 3}
          shadow-mapSize-width={512}
          shadow-mapSize-height={512}
          shadow-bias={-0.001}
          power={power}
        />
        
        <pointLight 
          ref={candleLight2}
          position={[0, 4, 0]}
          color={0xffaa33}
          intensity={1}
          distance={10}
          decay={2}
          castShadow={index < 3}
          shadow-mapSize-width={512}
          shadow-mapSize-height={512}
          shadow-bias={-0.001}
          power={power}
        />
      </group>
    </group>
  );
}

// Group of candles
function CandleInstances({ positions, candlePower = 40, visibleCount = 0 }) {
  return (
    <group>
      {positions.map((pos, i) => (
        <FadeIn key={`candle-fade-${i}`} start={i < visibleCount} duration={1}>
          <Candle 
            position={pos} 
            index={i} 
            power={candlePower * (i === 0 ? 1 : 0.6)} // Main candle is brighter
          />
        </FadeIn>
      ))}
    </group>
  );
}

// Painting Frame component
function PaintingFrame({ position = [0, 2.3, -0.85], rotation = [0, 0, 0], scale = 1, stepAnimation = 1 }) {
  // Define frame dimensions
  const frameWidth = 4.5;
  const frameHeight = 2.6;
  const frameDepth = 0.1;
  const borderWidth = 0.15;
  
  // Create initial simple material
  const initialMaterial = useMemo(() => new THREE.MeshStandardMaterial({
    color: stepAnimation === 1 ? 0x000000 : 0xaaaaaa, // Tan color for light wooden appearance
    roughness: 0.8,
    metalness: 0.2
  }), []);

  // Create frame material for wooden appearance with texture maps
  const frameMaterial = useMemo(() => new THREE.MeshStandardMaterial({
    color: stepAnimation === 1 ? 0xD2B48C : 0xaaaaaa, // Tan color for light wooden appearance
    roughness: 0.8,
    metalness: 0.2
  }), []);
  
  // State to track which material to use
  const [currentMaterial, setCurrentMaterial] = useState(initialMaterial);
  
  // Force material update when stepAnimation changes
  useEffect(() => {
    const newColor = stepAnimation === 1 || stepAnimation >= 5 ? 0xD2B48C : 
                     stepAnimation === 2 ? 0xaaaaaa :
                     stepAnimation === 3 ? 0x8B4513 :
                     0x4B0082;
    initialMaterial.color.set(newColor);
    initialMaterial.needsUpdate = true;
    frameMaterial.color.set(newColor);
    frameMaterial.needsUpdate = true;
  }, [stepAnimation, initialMaterial, frameMaterial]);
  
  // Load textures for the frame
  useEffect(() => {
    const textureLoader = new THREE.TextureLoader();
    
    // Load the wood texture for bump and roughness maps
    textureLoader.load('/wood.jpeg', (woodTexture) => {
      // Configure texture settings
      woodTexture.wrapS = THREE.RepeatWrapping;
      woodTexture.wrapT = THREE.RepeatWrapping;
      woodTexture.anisotropy = 4;
      woodTexture.repeat.set(3, 3);
      
      // Create a clone for the bump map
      const bumpTexture = woodTexture.clone();
      
      // Apply textures to the material
      frameMaterial.bumpMap = bumpTexture;
      frameMaterial.bumpScale = 3;
      frameMaterial.roughnessMap = woodTexture;
      frameMaterial.needsUpdate = true;

      // Apply the textured material after 3 seconds
      setTimeout(() => {
        setCurrentMaterial(frameMaterial);
      }, 3000);
    });
  }, [frameMaterial]);
  
  return (
    <group position={position} rotation={rotation} scale={scale}>
      {/* Top border */}
      <mesh castShadow position={[0, frameHeight/2 - borderWidth/2, 0]}>
        <boxGeometry args={[frameWidth, borderWidth, frameDepth]} />
        <primitive object={currentMaterial} attach="material" />
      </mesh>
      
      {/* Bottom border */}
      <mesh castShadow position={[0, -frameHeight/2 + borderWidth/2, 0]}>
        <boxGeometry args={[frameWidth, borderWidth, frameDepth]} />
        <primitive object={currentMaterial} attach="material" />
      </mesh>
      
      {/* Left border */}
      <mesh castShadow position={[-frameWidth/2 + borderWidth/2, 0, 0]}>
        <boxGeometry args={[borderWidth, frameHeight - 2*borderWidth, frameDepth]} />
        <primitive object={currentMaterial} attach="material" />
      </mesh>
      
      {/* Right border */}
      <mesh castShadow position={[frameWidth/2 - borderWidth/2, 0, 0]}>
        <boxGeometry args={[borderWidth, frameHeight - 2*borderWidth, frameDepth]} />
        <primitive object={currentMaterial} attach="material" />
      </mesh>
    </group>
  );
}

// Camera rig component that responds to mouse movement and scroll
function CameraRig({ children, setStepAnimation }) {
  const { camera } = useThree();
  const group = useRef();
  const mouse = useRef([0, 0]);
  const hasTransitioned = useRef(false);
  const isTransitioning = useRef(false);
  const transitionProgress = useRef(0);
  const initialPosition = useRef(new THREE.Vector3(-5, 2, 12));
  const tvFocusPosition = useRef(new THREE.Vector3(0, 1.5, 2)); // Closer position focused on TV
  
  // Save the initial camera position on first render
  useEffect(() => {
    initialPosition.current.copy(camera.position);
  }, [camera]);
  
  // Update mouse coordinates on mouse move
  useEffect(() => {
    const handleMouseMove = (event) => {
      // Calculate normalized mouse coordinates (-1 to 1)
      mouse.current = [
        (event.clientX / window.innerWidth) * 2 - 1,
        -(event.clientY / window.innerHeight) * 2 + 1
      ];
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);
  
  // Add scroll event listener that triggers once
  useEffect(() => {
    const handleScroll = (event) => {
      // Only transition if we haven't already and aren't currently transitioning
      if (!hasTransitioned.current && !isTransitioning.current) {
        // Trigger the step animation change immediately when scrolling starts
        if (setStepAnimation) {
          setStepAnimation(2);
        }
        
        isTransitioning.current = true;
        transitionProgress.current = 0;
        
        // Remove wheel event prevention after starting transition
        // This allows normal scrolling to take over after camera moves
        setTimeout(() => {
          document.removeEventListener('wheel', preventDefault);
        }, 800); // Wait for transition to complete before enabling page scroll
      }
    };
    
    const preventDefault = (e) => {
      e.preventDefault();
      handleScroll(e);
    };
    
    // Add wheel event listener with preventDefault to stop page scrolling during initial state
    document.addEventListener('wheel', preventDefault, { passive: false });
    
    return () => {
      document.removeEventListener('wheel', preventDefault);
    };
  }, [setStepAnimation]);
  
  // Apply mouse movement and handle transition
  useFrame(({ clock }, delta) => {
    if (!group.current) return;
    
    // Increased multipliers for more dramatic mouse movement
    const mouseX = mouse.current[0] * 1.2; 
    const mouseY = mouse.current[1] * 0.8;
    
    // Add subtle oscillation for more dynamic movement
    const time = clock.getElapsedTime();
    const oscillation = Math.sin(time * 0.5) * 0.02;
    
    // Handle camera position transition
    if (isTransitioning.current) {
      // Progress the transition (complete transition in 0.5 seconds)
      transitionProgress.current = Math.min(1, transitionProgress.current + delta * 2);
      
      // Apply the transition with easing
      const easedProgress = easeOutCubic(transitionProgress.current);
      
      // Interpolate camera position
      camera.position.lerpVectors(initialPosition.current, tvFocusPosition.current, easedProgress);
      
      // Mark transition as complete when done
      if (transitionProgress.current === 1) {
        isTransitioning.current = false;
        hasTransitioned.current = true; // Mark that we've done the transition
      }
    }
    
    // Apply mouse movement with intensity based on camera position
    const inClosePosition = hasTransitioned.current || isTransitioning.current;
    
    group.current.rotation.y = THREE.MathUtils.lerp(
      group.current.rotation.y,
      -mouseX * (inClosePosition ? 0.06 : 0.12) + oscillation,
      0.07
    );
    
    group.current.rotation.x = THREE.MathUtils.lerp(
      group.current.rotation.x,
      -mouseY * (inClosePosition ? 0.04 : 0.08) + oscillation * 0.5,
      0.07
    );
    
    // Add subtle position shift for more immersive effect (reduced when close to TV)
    if (!inClosePosition) {
      group.current.position.x = THREE.MathUtils.lerp(
        group.current.position.x,
        -mouseX * 0.3,
        0.04
      );
      
      group.current.position.y = THREE.MathUtils.lerp(
        group.current.position.y,
        mouseY * 0.2,
        0.04
      );
    } else {
      // Reset position shifts when focusing on TV
      group.current.position.x = THREE.MathUtils.lerp(
        group.current.position.x,
        0,
        0.04
      );
      
      group.current.position.y = THREE.MathUtils.lerp(
        group.current.position.y,
        0,
        0.04
      );
    }
  });
  
  // Easing function for smoother transitions
  const easeOutCubic = (x) => {
    return 1 - Math.pow(1 - x, 3);
  };
  
  return <group ref={group}>{children}</group>;
}

// Add this new component before the Scene component
function Screen({ position = [0, 2.3, -0.85], rotation = [0, 0, 0], scale = 1, stepAnimation = 1 }) {
  const screenMaterial = useMemo(() => new THREE.MeshStandardMaterial({
    color: 0x000000,
    roughness: 1,
    metalness: 0
  }), []);
  
  // Create refs to store preloaded elements
  const videoRef = useRef(null);
  const videoTextureRef = useRef(null);
  const videoMaterialRef = useRef(null);

  // Preload the video when component mounts
  useEffect(() => {
    // Create and preload video element
    const video = document.createElement('video');
    video.src = '/video.mp4';
    video.crossOrigin = 'anonymous';
    video.loop = true;
    video.muted = true;
    video.playsInline = true;
    video.load(); // Start loading the video
    
    // Create video texture
    const videoTexture = new THREE.VideoTexture(video);
    videoTexture.colorSpace = THREE.SRGBColorSpace;
    //videoTexture.encoding = THREE.sRGBEncoding; // For color space handling

    
    // Create material with video texture
    const videoMaterial = new THREE.MeshStandardMaterial({
      map: videoTexture,
      emissive: new THREE.Color(0xffffff), // Preto - sem emissão
      emissiveMap: videoTexture, // Sem mapa emissivo
      emissiveIntensity: 0.2, // Intensidade zero
      toneMapped: false,
      transparent: false
    });
    
    // Store references for later use
    videoRef.current = video;
    videoTextureRef.current = videoTexture;
    videoMaterialRef.current = videoMaterial;
    
    return () => {
      // Cleanup
      if (video) {
        video.pause();
        video.src = '';
        video.load();
      }
    };
  }, []);

  // Handle material changes based on stepAnimation
  useEffect(() => {
    if (stepAnimation === 2 && videoMaterialRef.current && videoRef.current) {
      // Apply the preloaded video material without delay
      screenMaterial.copy(videoMaterialRef.current);
      screenMaterial.needsUpdate = true;
      videoRef.current.play().catch(e => console.error("Video play failed:", e));
    } else {
      const textureLoader = new THREE.TextureLoader();
      const frameImage = '/frame.jpg';
      
      // Load the screen texture
      textureLoader.load(frameImage, (texture) => {
        texture.wrapS = THREE.ClampToEdgeWrapping;
        texture.wrapT = THREE.ClampToEdgeWrapping;
        texture.flipZ = false;
        texture.center = new THREE.Vector2(0.5, 0.5);
        texture.colorSpace = THREE.SRGBColorSpace;

        // Create emissive material
        const emissiveMaterial = new THREE.MeshStandardMaterial({
          map: texture,
          emissive: new THREE.Color(0xffffff),
          emissiveMap: texture,
          emissiveIntensity: 0.8,
          toneMapped: false,
          transparent: false
        });

        // Apply the material after 3 seconds
        setTimeout(() => {
          screenMaterial.copy(emissiveMaterial);
          screenMaterial.needsUpdate = true;
        }, 3000);
      });
    }
  }, [screenMaterial, stepAnimation]);

  return (
    <mesh 
      position={[position[0], position[1], position[2] - 0.01]} // Slightly in front of the frame
      rotation={[Math.PI, ...rotation]} // Add Math.PI to flip the plane vertically
      scale={[4.5 * scale, 2.6 * scale, 1]} // Match frame dimensions
    >
      <planeGeometry />
      <primitive object={screenMaterial} attach="material" />
    </mesh>
  );
}

// Add Wand component before the Scene component
// function Wand({ position = [0, 3, -2], rotation = [0, 0, 0], scale = .1 }) {
//   const obj = useLoader(OBJLoader, '/wand.obj');
//   const wandRef = useRef();

//   useEffect(() => {
//     if (obj) {
//       // Scale and position the wand
//       obj.scale.set(0.01, 0.01, 0.01);
//       obj.position.set(1.5, 2, 0);
//       obj.rotation.set(-2, 2, 0);
      
//       // Add material to all meshes in the object
//       obj.traverse((child) => {
//         if (child.isMesh) {
//           child.material = new THREE.MeshStandardMaterial({
//             color: 0x8B4513, // Brown color for the wand
//             roughness: 0.7,
//             metalness: 0.3
//           });
//           child.castShadow = true;
//         }
//       });
//     }
//   }, [obj]);

//   return (
//     <primitive 
//       ref={wandRef}
//       object={obj} 
//       position={position}
//       rotation={rotation}
//       scale={scale}
//     />
//   );
// }

// Scene setup component
// Updated Scene component with proper background integration
function Scene({ sceneParams, setSceneParams, revealStep = 0, stepAnimation = 1, setStepAnimation }) {
  const { gl, scene, camera } = useThree();
  const orbitControlsRef = useRef();
  
  const candlePositions = useMemo(() => {
    const positions = [
      // Ground level candles
      [-1, 4, -4],      // Back left corner
      [4.8, 1, -3],       // Back right corner
      // [-4, 0, 4],       // Front left corner
      // [4, 0, 4],        // Front right corner
      // [-5, 0, 0],       // Left middle
      [2, 1,0.5],        // Right middle
      [-1, 4, -7],       // Back middle
      // [0, 0, 5],        // Front middle
      [2, 5, -3],       // Additional position
      
      // Higher candles
      // [-3.0, 1.2, -2.5],  // Elevated back left
      [2.6, 4, -3.5],   // Elevated back right
      // [-3.5, 1.5, 3.5],   // Elevated front left
      // [3.5, 1.5, 3.5],    // Elevated front right
      [-2, 3.5, 2],     // Elevated left middle
      [0, -3, -2],      // Elevated right middle
      [0, 1.5, -4.5],     // Elevated back middle
      // [0, 1.5, 4.5],      // Elevated front middle
      [2.5, 1.5, -2.5],   // Elevated additional
      
      // Even higher candles
      [-3, 3, -3],        // High back left
      [3, 3, -3],         // High back right
      // [-3, 3, 3],         // High front left
      // [3, 3, 3],          // High front right
      [-4, 3, 0],         // High left middle
      [4, 3, 0],          // High right middle
      [0, 3, -4],         // High back middle
      [0, 3, 4],          // High front middle
      [2, 3, -2],         // High additional
      [-2, 3, 2],         // High additional 2
    ];
    // Only return the number of candles specified in sceneParams
    return positions.slice(0, sceneParams.candles);
  }, [sceneParams.candles]);

  
  // Update parent component state
  useEffect(() => {
    setSceneParams?.(sceneParams);
  }, [sceneParams, setSceneParams]);

  // Luminous powers (from reference, adjusted for candle)
  const candleLuminousPowers = {
    'Bright Candle (100 lm)': 100,
    'Standard Candle (40 lm)': 40,
    'Dim Candle (15 lm)': 15,
    'Faint Candle (5 lm)': 10,
    'Off': 0
  };

  // Hemisphere light irradiances (from reference)
  const hemiLuminousIrradiances = {
    '0.0001 lx (Moonless Night)': 0.0001,
    '0.002 lx (Night Airglow)': 0.002,
    '0.5 lx (Full Moon)': 0.5,
    '3.4 lx (City Twilight)': 3.4,
    '50 lx (Living Room)': 50,
    '100 lx (Very Overcast)': 100,
    '350 lx (Office Room)': 350,
    '400 lx (Sunrise/Sunset)': 400,
    '1000 lx (Overcast)': 1000,
    '18000 lx (Daylight)': 18000,
    '50000 lx (Direct Sun)': 50000
  };
  
  // Calculate how many candles should be visible based on revealStep
  const totalCandles = candlePositions.length;
  let visibleCandles = 0;
  if (revealStep >= 1) {
    visibleCandles = Math.min(totalCandles, Math.max(1, revealStep - 1));
  }

  // Step at which painting & TV appear
  const paintingStep = totalCandles + 2; // after all candles + cube

  return (
    <>
      {/* Wrap the scene with CameraRig */}
      <CameraRig setStepAnimation={setStepAnimation}>
        {/* Fade-in wrappers for smooth opacity transition */}
        {/* 1. First candle only */}
        <FadeIn start={revealStep >= 1}><CandleInstances 
          positions={candlePositions} 
          candlePower={candleLuminousPowers[sceneParams.candlePower]} 
          visibleCount={visibleCandles}
        /></FadeIn>

        {/* 2. Cubes appear after the first candle */}
        <FadeIn start={revealStep >= 2}><Cubes /></FadeIn>

        {/* Painting frame + TV after all candles are visible */}
        <FadeIn start={revealStep >= paintingStep}>
          <Screen stepAnimation={stepAnimation} />
        </FadeIn>
        <PaintingFrame stepAnimation={stepAnimation} />

        <Television enableScreenMaterial={revealStep >= paintingStep + 1} />

        {/* Add Wand with 5-second delay */}
        {/* <FadeIn start={wandVisible}>
          <Wand position={[2, 2, -2]} rotation={[0, Math.PI / 4, 0]} scale={1} />
        </FadeIn> */}

        {/* Floor stays always visible */}
        <Floor />
        
        {/* Global hemisphere light */}
        <hemisphereLight 
          color={0xddeeff} 
          groundColor={0x0f0e0d} 
          intensity={hemiLuminousIrradiances[sceneParams.hemiIrradiance]} 
        />
      </CameraRig>
      
      {/* Camera controls with ref for access */}
      <OrbitControls 
        ref={orbitControlsRef}
        target={[-.5, 2, 0]}
        minDistance={2} 
        maxDistance={50}
        enableDamping
        enableZoom={false}
        dampingFactor={0.05}
      />
    </>
  );
}

// Main component
// Main component with fixes
const PhysicalLightScene = () => {
  // Track scene parameters at the top level
  const [isClickable, setIsClickable] = useState(false);
  const [stepAnimation, setStepAnimation] = useState(1);
  const [sceneParams, setSceneParams] = useState({
    shadows: true,
    exposure: 0.8,
    candlePower: 'Standard Candle (40 lm)',
    hemiIrradiance: '0.0001 lx (Moonless Night)',
    candles: 8,
    quality: 'low',
    showStats: false,
    visibleCandles: 8
  });

    // Add effect for wand delay
    useEffect(() => {
      const timer = setTimeout(() => {
        setIsClickable(true);
      }, 15000); // 15 second delay
      
      return () => clearTimeout(timer);
    }, []);

  // State to track if panorama is loaded
  const [panoramaLoaded, setPanoramaLoaded] = useState(false);
  
  // Luminous powers (from reference, adjusted for candle)
  const candleLuminousPowers = {
    'Bright Candle (100 lm)': 100,
    'Standard Candle (40 lm)': 40,
    'Dim Candle (15 lm)': 15,
    'Faint Candle (5 lm)': 10,
    'Off': 0
  };

  // Hemisphere light irradiances (from reference)
  const hemiLuminousIrradiances = {
    '0.0001 lx (Moonless Night)': 0.0001,
    '0.002 lx (Night Airglow)': 0.002,
    '0.5 lx (Full Moon)': 0.5,
    '3.4 lx (City Twilight)': 3.4,
    '50 lx (Living Room)': 50,
    '100 lx (Very Overcast)': 100,
    '350 lx (Office Room)': 350,
    '400 lx (Sunrise/Sunset)': 400,
    '1000 lx (Overcast)': 1000,
    '18000 lx (Daylight)': 18000,
    '50000 lx (Direct Sun)': 50000
  };

  // Progressive reveal state
  const [revealStep, setRevealStep] = useState(0);

  useEffect(() => {
    // Make the progressive reveal start almost immediately and advance more quickly
    const initialDelay = 200; // start after 0.2s instead of 1s

    let step = 0;
    const startReveal = () => {
      const interval = setInterval(() => {
        step += 1;
        setRevealStep(step);
      }, 300); // advance every 0.3s to shorten space between animations

      // Clean-up when component unmounts
      return () => clearInterval(interval);
    };

    const timeoutId = setTimeout(startReveal, initialDelay);

    return () => clearTimeout(timeoutId);
  }, []);

  return (
    <div style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0 }}>
      <Suspense fallback={<div>Loading...</div>}>
        <Canvas
          shadows 
          camera={{ fov: 60, near: 0.1, far: 200, position: [-5, 2, 12] }}
          dpr={Math.min(window.devicePixelRatio, 1.5)} 
          performance={{ min: 0.5 }} 
          gl={{ 
            powerPreference: 'high-performance', 
            antialias: true,
            stencil: false,
            depth: true
          }}
        >
          <Scene 
            sceneParams={sceneParams} 
            setSceneParams={setSceneParams} 
            revealStep={revealStep}
            stepAnimation={stepAnimation}
            setStepAnimation={setStepAnimation}
          />
          {sceneParams.showStats && <Stats />}
        </Canvas>
      </Suspense>
      

      
      {/* Control panel outside Canvas - commented out for now */}
      {/* <ControlPanel 
        params={sceneParams} 
        setParams={setSceneParams} 
        candleLuminousPowers={candleLuminousPowers}
        hemiLuminousIrradiances={hemiLuminousIrradiances}
      /> */}
    </div>
  );
};

export default PhysicalLightScene;